import { GridColDef } from '@mui/x-data-grid';
import { Box, TextField, IconButton, InputAdornment, Button } from '@mui/material';
import { atom, useSetAtom } from 'jotai';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';

import { useMemo, useState, useEffect } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import AddWalletAmount from './AddWalletAmount';
import { useManageWallet } from './useManageWallet';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import { useDebounce } from '../Utils/useDounce';
import { useWallet } from '../../Query/WalletQuery';
import { usePermissions } from '../../../context/PermissionContext';

export interface DeliveryManagerFormValues {
  id?: string;
  question: string;
  questionInArabic: string;
  answer: string;
  answerInArabic: string;
}

export const deliveryManagerAtom = atom<DeliveryManagerFormValues | null>(null);

export const walletAtom = atom<any | null>(null);

const WalletTable = () => {
  const setDeliveryManagerAtom = useSetAtom(deliveryManagerAtom);
  const { hasPermission } = usePermissions();

  const setWalletAtom = useSetAtom(walletAtom);
  // const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'deduct'>('add');
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);


  const { params, dispatch } = useManageWallet();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: axiosResponse, isLoading, refetch } = useWallet(memoizedParams);

  const walletList = axiosResponse?.data || [];

  const filteredRows = React.useMemo(() => {
    return walletList.map((row: any, index: number) => {
      const sno = index + 1 + (Number(params.page - 1)) * Number(params.limit);
      return {
        ...row,
        id: row.customerId,
        name: row.customerName,
        email: row.customerEmail,
        totalTransactions: row.totalTransactions,
        availableWalletAmount: row.availableWalletBalance,
        sno,
      } as any;
    });
  }, [walletList, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // const handleAddSubAdmin = () => {
  //   setOpenAddDialog(true);
  // };

  // const handleCloseAddDialog = () => {
  //   setOpenAddDialog(false);
  // };

  const handleEditClick = (row: any, mode: 'add' | 'deduct') => {
    // const faqData = {
    //   id: row.id,
    //   question: row.question,
    //   questionInArabic: row.questionInArabic,
    //   answer: row.answer,
    //   answerInArabic: row.answerInArabic,
    // };
    setWalletAtom(row);
    setModalMode(mode);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setDeliveryManagerAtom(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
    },
    {
      field: 'name',
      headerName: 'Customer Name',
      flex: 1, // moderately sized
    },
    {
      field: 'email',
      headerName: 'Customer Email',
      flex: 1, // moderately sized
    },
    {
      field: 'totalTransactions',
      headerName: 'Total Transactions',
      flex: 1, // moderately sized
    },
    {
      field: 'availableWalletAmount',
      headerName: 'Available Wallet Amount',
      flex: 1, // moderately sized
    },
    {
      field: 'add',
      headerName: 'Add',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        if (!hasPermission('wallet', 'Credit')) {
          return null;
        }
        return (
          <IconButton
            size="small"
            onClick={() => {
              handleEditClick(params.row, 'add');
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Deduct',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        if (!hasPermission('wallet', 'Debit')) {
          return null;
        }
        return (
          <IconButton
            size="small"
            onClick={() => {
              handleEditClick(params.row, 'deduct');
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        );
      },
    },
    {
      field: 'history',
      headerName: 'History',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="text"
          color="secondary"
          size="small"
          onClick={() => {
            navigate('/wallet-history', { state: { wallet: params.row } });
          }}
        >
          History
        </Button>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}

          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search Customer Name, Email..."
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSubAdmin}
              size="small"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: COLORS.primary.main,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: COLORS.primary.main,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              Add Wallet
            </Button> */}
          </Box>
        </Box>

        {/* <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add wallet amount"
        >
          <AddWalletAmount onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal> */}

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={
            modalMode === 'add' ? 'Add wallet amount' : 'Deduct wallet amount'
          }
        >
          <AddWalletAmount onCancel={handleCloseEditDialog} mode={modalMode} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={axiosResponse?.pagination?.totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
        />
      </Box>
    </>
  );
};

export default WalletTable;
